import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMe, useMyTenants } from "../api/hooks";
import { useActiveTenantId, useSetActiveTenantId } from "../api/tenant-context";
import type { MyTenant } from "@rua-watch/shared";

// Workspace picker rendered in the Sidebar.
//
// Hidden when the user has exactly one workspace and isn't a platform
// admin — no point showing a "switcher" of one. The button always shows
// the currently-active workspace (resolved from localStorage with a
// fallback to whatever the server picked).
//
// Switching: writes the new id to localStorage, then invalidates every
// React Query so the next render fetches under the new tenant. We keep
// it in-page rather than a hard reload because the dashboard widgets
// already handle suspense/loading states.

export function OrgSwitcher() {
  const me = useMe();
  const tenants = useMyTenants();
  const activeId = useActiveTenantId();
  const setActive = useSetActiveTenantId();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const isPlatformAdmin = me.data?.isPlatformAdmin ?? false;
  const rows = tenants.data ?? [];
  const resolvedId = activeId ?? me.data?.activeTenantId ?? null;
  const current = rows.find((t) => t.id === resolvedId) ?? rows[0];

  // Single-tenant member with no platform powers → no need to render
  // anything. Saves vertical space in the sidebar.
  if (!isPlatformAdmin && rows.length <= 1) return null;
  if (!current) return null;

  const onPick = (id: number) => {
    setActive(id);
    setOpen(false);
    // Reset every cached query so widgets refetch under the new tenant.
    // Cheap because tanstack's per-key caching means in-flight queries
    // for the new tenant share their own keys.
    qc.invalidateQueries();
  };

  return (
    <div className="relative px-2 pt-3" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-left text-sm text-slate-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{current.displayName}</span>
          <span className="truncate text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {current.plan} · {current.role ?? "platform"}
          </span>
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-3.5 w-3.5 shrink-0 text-slate-400"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open ? (
        <div className="absolute left-2 right-2 z-20 mt-1 max-h-80 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-1 shadow-lg">
          {rows.map((t) => (
            <Row
              key={t.id}
              tenant={t}
              active={t.id === resolvedId}
              onPick={() => onPick(t.id)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Row({
  tenant,
  active,
  onPick,
}: {
  tenant: MyTenant;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={
        "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800/70 " +
        (active ? "bg-slate-100 dark:bg-slate-800" : "")
      }
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-slate-800 dark:text-slate-200">{tenant.displayName}</span>
        <span className="truncate text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
          {tenant.plan} · {tenant.role ?? "platform"}
        </span>
      </span>
      {tenant.status !== "active" ? (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          {tenant.status}
        </span>
      ) : null}
    </button>
  );
}
